// v2 snake — a continuous coiled weave: tapering body cylinders laid end-to-end
// along a serpentine S-path (each aligned to its local heading so the body reads
// as one tube), dark saddle blotches riding the coils, a neck rearing up into a
// flat wedge head with golden eyes and a flicked red tongue, and a pointed tail
// tip trailing off the last coil. Two skins: grass green and a sandy adder.
// Ported 1:1 from age-of-mcp's v2/snake.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const SKINS = [
  { base: "#4e7c34", baseHi: "#5c8c3e", blotch: "#31511e", head: "#5a8a3c", eye: "#e0c030", tongue: "#c83a3a" },
  { base: "#9a8054", baseHi: "#a8905e", blotch: "#5c482a", head: "#a08a5c", eye: "#d8a830", tongue: "#b83232" },
];

// The weave: path points head→tail, x marching back, z swinging in an S.
const N = 9;

function snakeParts(v: number): Part[] {
  const c = SKINS[v % SKINS.length]!;
  const parts: Part[] = [];

  const pts: Array<{ x: number; z: number; r: number }> = [];
  for (let i = 0; i < N; i++) {
    pts.push({ x: 0.05 - i * 0.019, z: Math.sin(i * 0.85) * 0.022, r: 0.0125 - i * 0.001 });
  }
  // Body segments: a tapered cylinder per path step, rotated so its axis
  // follows the local heading ([0, -th, -PI/2] lays the +y axis flat at
  // azimuth `th`), overlapping so the coil reads as one continuous tube.
  for (let i = 0; i < N - 1; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const th = Math.atan2(b.z - a.z, b.x - a.x);
    parts.push({
      geometry: new THREE.CylinderGeometry(b.r, a.r, len * 1.3, 5),
      color: i % 2 === 0 ? c.base : c.baseHi,
      position: [(a.x + b.x) / 2, ((a.r + b.r) / 2) * 0.95, (a.z + b.z) / 2],
      rotation: [0, -th, -Math.PI / 2],
    });
  }
  // Dark saddle blotches riding the top of the coils.
  for (const i of [1, 3, 5, 7]) {
    const p = pts[i]!;
    parts.push({
      geometry: new THREE.IcosahedronGeometry(p.r * 0.72, 0), color: c.blotch,
      position: [p.x, p.r * 1.5, p.z], scale: [1.3, 0.55, 1.3],
    });
  }
  // Pointed tail tip trailing off the last coil along its heading.
  const t1 = pts[N - 2]!, t2 = pts[N - 1]!;
  const tth = Math.atan2(t2.z - t1.z, t2.x - t1.x);
  parts.push({
    geometry: new THREE.ConeGeometry(t2.r, 0.024, 4), color: c.base,
    position: [t2.x + Math.cos(tth) * 0.01, t2.r * 0.9, t2.z + Math.sin(tth) * 0.01],
    rotation: [0, -tth, -Math.PI / 2],
  });
  parts.push(
    // neck rearing up off the first coil
    { geometry: new THREE.CylinderGeometry(0.008, 0.0105, 0.034, 5), color: c.head,
      position: [0.063, 0.019, 0], rotation: [0, 0, -0.78] },
    // flat wedge head with a blunt snout
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.head,
      position: [0.082, 0.0295, 0], scale: [1.45, 0.62, 1.15] },
    { geometry: new THREE.ConeGeometry(0.0075, 0.016, 4), color: c.head,
      position: [0.0995, 0.028, 0], rotation: [0, 0, -Math.PI / 2] },
    // golden eyes
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: c.eye,
      position: [0.0875, 0.0345, 0.009] },
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: c.eye,
      position: [0.0875, 0.0345, -0.009] },
    // flicked red tongue
    { geometry: new THREE.ConeGeometry(0.0016, 0.013, 3), color: c.tongue,
      position: [0.112, 0.028, 0], rotation: [0, 0, -Math.PI / 2] },
  );
  return parts;
}

export const snakeV2 = defineModel({
  id: "snake-v2",
  batchId: "fable",
  name: "Snake (v2)",
  tags: ["creature","snake","low-poly","age-of-mcp","fable"],
  summary: "Low-poly snake.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => snakeParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => snakeParts(1) },
  ],
  animations: [],
});
